import { useEffect, useState } from 'react';
import { Plus, X, Check, RotateCcw, Trash2, CalendarClock, Pencil  } from 'lucide-react';
import api from '../services/api';

const estadoLabel = { PENDIENTE: 'Pendiente', COMPLETADA: 'Completada', REPROGRAMADA: 'Reprogramada' };
const estadoColor = {
  PENDIENTE: { bg: '#eff6ff', text: '#2563eb' },
  COMPLETADA: { bg: '#f0fdf4', text: '#16a34a' },
  REPROGRAMADA: { bg: '#fff7ed', text: '#ea580c' },
};
const prioridadColor = {
  BAJA: { bg: '#f1f5f9', text: '#64748b' },
  MEDIA: { bg: '#eff6ff', text: '#2563eb' },
  ALTA: { bg: '#fef2f2', text: '#dc2626' },
};
const motivoLabel = { PROPIO: 'Propio', CLIENTE: 'Cliente', EXTERNO: 'Externo' };

export default function ActividadesPage() {
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reprogramando, setReprogramando] = useState(null); // actividad seleccionada
  const [filtroEstado, setFiltroEstado] = useState('');
  const [form, setForm] = useState({ titulo: '', descripcion: '', fechaLimite: '', prioridad: 'MEDIA' });
  const [reprogramaForm, setReprogramaForm] = useState({ fechaNueva: '', motivo: '', comentario: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editando, setEditando] = useState(null); // actividad seleccionada para editar
  const [editForm, setEditForm] = useState({ titulo: '', descripcion: '', prioridad: 'MEDIA' });

  const fetchActividades = async () => {
    const res = await api.get('/actividades');
    setActividades(res.data);
  };

  useEffect(() => { fetchActividades().finally(() => setLoading(false)); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/actividades', form);
      setShowForm(false);
      setForm({ titulo: '', descripcion: '', fechaLimite: '', prioridad: 'MEDIA' });
      fetchActividades();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear actividad');
    } finally { setSubmitting(false); }
  };

  const handleCompletar = async (id) => {
    await api.patch(`/actividades/${id}/completar`);
    fetchActividades();
  };

  const handleReabrir = async (id) => {
    await api.patch(`/actividades/${id}/reabrir`);
    fetchActividades();
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    await api.delete(`/actividades/${id}`);
    fetchActividades();
  };

  const handleReprogramar = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch(`/actividades/${reprogramando.id}/reprogramar`, reprogramaForm);
      setReprogramando(null);
      setReprogramaForm({ fechaNueva: '', motivo: '', comentario: '' });
      fetchActividades();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reprogramar');
    } finally { setSubmitting(false); }
  };

  const abrirEdicion = (actividad) => {
    setEditando(actividad);
    setEditForm({
      titulo: actividad.titulo,
      descripcion: actividad.descripcion || '',
      prioridad: actividad.prioridad
    });
  };

  const handleEditar = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch(`/actividades/${editando.id}`, editForm);
      setEditando(null);
      fetchActividades();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al editar actividad');
    } finally { setSubmitting(false); }
  };

  const visibles = filtroEstado ? actividades.filter(a => a.estado === filtroEstado) : actividades;

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Modal nueva actividad */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Nueva actividad</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Título</label>
                <input type="text" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} required
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
                  placeholder="¿Qué pendiente tienes?" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Descripción (opcional)</label>
                <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Fecha límite (opcional)</label>
                <input type="date" value={form.fechaLimite} onChange={e => setForm({ ...form, fechaLimite: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Prioridad</label>
                <select value={form.prioridad} onChange={e => setForm({ ...form, prioridad: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="BAJA">Baja</option>
                  <option value="MEDIA">Media</option>
                  <option value="ALTA">Alta</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal reprogramar */}
      {reprogramando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Reprogramar</h2>
              <button onClick={() => setReprogramando(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>{reprogramando.titulo}</p>
            <form onSubmit={handleReprogramar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nueva fecha</label>
                <input type="date" value={reprogramaForm.fechaNueva} onChange={e => setReprogramaForm({ ...reprogramaForm, fechaNueva: e.target.value })} required
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Motivo (opcional)</label>
                <select value={reprogramaForm.motivo} onChange={e => setReprogramaForm({ ...reprogramaForm, motivo: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="">Sin especificar</option>
                  <option value="PROPIO">Propio</option>
                  <option value="CLIENTE">Cliente</option>
                  <option value="EXTERNO">Externo</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Comentario (opcional)</label>
                <textarea value={reprogramaForm.comentario} onChange={e => setReprogramaForm({ ...reprogramaForm, comentario: e.target.value })} rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }}
                  placeholder="¿Por qué se reprograma?" />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setReprogramando(null)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#ea580c', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Guardando...' : 'Reprogramar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editando && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
        <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Editar actividad</h2>
            <button onClick={() => setEditando(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
          </div>
          <form onSubmit={handleEditar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Título</label>
              <input type="text" value={editForm.titulo} onChange={e => setEditForm({ ...editForm, titulo: e.target.value })} required
                style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Descripción</label>
              <textarea value={editForm.descripcion} onChange={e => setEditForm({ ...editForm, descripcion: e.target.value })} rows={3}
                style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Prioridad</label>
              <select value={editForm.prioridad} onChange={e => setEditForm({ ...editForm, prioridad: e.target.value })}
                style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                <option value="BAJA">Baja</option>
                <option value="MEDIA">Media</option>
                <option value="ALTA">Alta</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button type="button" onClick={() => setEditando(null)}
                style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                Cancelar
              </button>
              <button type="submit" disabled={submitting}
                style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                {submitting ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Mis actividades</h1>
        <button onClick={() => setShowForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={15} /> Nueva actividad
        </button>
      </div>

      {/* Filtro por estado */}
      <div style={{ marginBottom: 16 }}>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'white' }}>
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="REPROGRAMADA">Reprogramada</option>
          <option value="COMPLETADA">Completada</option>
        </select>
      </div>

      {/* Lista de actividades */}
      {visibles.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No hay actividades</p>}
      {visibles.map(a => {
        const ec = estadoColor[a.estado] || {};
        const pc = prioridadColor[a.prioridad] || {};
        const completada = a.estado === 'COMPLETADA';
        return (
          <div key={a.id}
            style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: 16, marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', opacity: completada ? 0.65 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ background: ec.bg, color: ec.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{estadoLabel[a.estado]}</span>
                  <span style={{ background: pc.bg, color: pc.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{a.prioridad}</span>
                </div>
                <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#1e293b', textDecoration: completada ? 'line-through' : 'none' }}>{a.titulo}</p>
                {a.descripcion && <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b' }}>{a.descripcion}</p>}
                {a.fechaLimite && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
                    <CalendarClock size={13} />
                    {new Date(a.fechaLimite).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                )}
                {a.reprogramaciones?.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Historial de reprogramación</p>
                    {a.reprogramaciones.map(r => (
                      <p key={r.id} style={{ margin: '2px 0', fontSize: 12, color: '#64748b' }}>
                        → {new Date(r.fechaNueva).toLocaleDateString('es-MX')} {r.motivo && `· ${motivoLabel[r.motivo]}`} {r.comentario && `· "${r.comentario}"`}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {!completada ? (
                <>
                  <button onClick={() => handleCompletar(a.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#16a34a', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Check size={13} /> Completar
                  </button>
                  <button onClick={() => setReprogramando(a)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff7ed', color: '#ea580c', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <CalendarClock size={13} /> Reprogramar
                  </button>
                </>
              ) : (
                <button onClick={() => handleReabrir(a.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <RotateCcw size={13} /> Reabrir
                </button>
              )}
              <button onClick={() => abrirEdicion(a)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Pencil size={13} /> Editar
              </button>
              <button onClick={() => handleEliminar(a.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Trash2 size={13} /> Eliminar
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}